import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Cookie Policy
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700"
        >
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              1. What Are Cookies?
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, analyzing how you use our site, and personalizing content.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              2. How We Use Cookies
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We use cookies for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-6">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
              <li><strong>Authentication Cookies:</strong> To keep you logged in and secure</li>
              <li><strong>Preference Cookies:</strong> To remember your settings and choices</li>
              <li><strong>Analytics Cookies:</strong> To understand how visitors use our site</li>
              <li><strong>Performance Cookies:</strong> To improve website speed and functionality</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              3. Types of Cookies We Use
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              3.1 Essential Cookies
            </h3>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Purpose:</strong> These cookies are necessary for the website to function and cannot be disabled.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Examples:</strong> Session management, security tokens, load balancing
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Duration:</strong> Session or up to 1 year
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              3.2 Authentication Cookies
            </h3>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Purpose:</strong> To keep you logged in and maintain your session security.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Examples:</strong> Login tokens, user session data
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Duration:</strong> Session or up to 30 days
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              3.3 Preference Cookies
            </h3>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Purpose:</strong> To remember your preferences and settings.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Examples:</strong> Theme preferences, language settings, layout choices
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Duration:</strong> Up to 1 year
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              3.4 Analytics Cookies
            </h3>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Purpose:</strong> To understand how visitors use our website and improve our service.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Examples:</strong> Page views, user behavior, performance metrics
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Duration:</strong> Up to 2 years
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              4. Third-Party Cookies
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We may use third-party services that also set cookies on our website:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-6">
              <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
              <li><strong>Social Media:</strong> For social login and sharing features</li>
              <li><strong>Payment Processors:</strong> For secure payment processing</li>
              <li><strong>CDN Services:</strong> For content delivery and performance optimization</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              5. Managing Your Cookie Preferences
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You have several options for managing cookies:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              5.1 Browser Settings
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Most web browsers allow you to control cookies through their settings. You can:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-6">
              <li>Block all cookies</li>
              <li>Allow cookies only from trusted sites</li>
              <li>Delete existing cookies</li>
              <li>Set preferences for different types of cookies</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              5.2 Cookie Consent
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              When you first visit our website, you'll see a cookie consent banner. You can choose which types of cookies to accept or decline. You can change these preferences at any time through our cookie settings.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              6. Impact of Disabling Cookies
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              If you choose to disable cookies, some features of our website may not work properly:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-6">
              <li>You may need to log in repeatedly</li>
              <li>Your preferences and settings may not be saved</li>
              <li>Some website features may not function correctly</li>
              <li>Personalized content may not be available</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              7. Cookie Retention Periods
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Different types of cookies are stored for different periods:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-6">
              <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Remain on your device for a set period</li>
              <li><strong>Authentication Cookies:</strong> Typically last 30 days or until you log out</li>
              <li><strong>Analytics Cookies:</strong> Usually retained for 1-2 years</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              8. Updates to This Policy
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              9. Contact Information
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300">
                Email: privacy@websitebuilder.com<br />
                Address: 123 Builder Street, Tech City, TC 12345<br />
                Phone: +1 (555) 123-4567<br />
                Data Protection Officer: dpo@websitebuilder.com
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              10. Cookie Settings
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              You can manage your cookie preferences at any time by clicking the "Cookie Settings" link in the footer of our website or by contacting us directly.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;
